/**
 * Crypto Clicker 게임 액션 정의
 * 모든 상태 변경은 액션을 통해서만 발생
 */

import type { ActionHandler } from '@app-games/core';
import type { GameState } from './state.js';
import { getCurrentCPS, getCurrentClickPower } from './state.js';
import { GENERATORS } from './config/generators.js';
import { getGeneratorCost } from './utils.js';
import {
  calculateBlockchainReward,
  canPrestige2,
  calculateMetaCoinReward,
  PRESTIGE_BUFFS,
  META_UPGRADES
} from './config/prestige.js';
import { GOLDEN_EVENTS, GOLDEN_CONFIG, getGoldenIntervalMultiplier } from './config/goldenEvents.js';
import { UPGRADES, isUpgradeUnlocked } from './config/upgrades.js';
import { MANIPULATIONS } from './config/market.js';
import { DARKPOOL_CONFIG, getRewardMultiplier, getMaxBetRatio, generateInsiderInfo } from './config/darkpool.js';

export type GameAction =
  | { type: 'CLICK' }
  | { type: 'DOGE_CLICK' }
  | { type: 'BUY_GENERATOR'; payload: { generatorId: string; count?: number } }
  | { type: 'BUY_UPGRADE'; payload: { upgradeId: string } }
  | { type: 'PRESTIGE' }
  | { type: 'PRESTIGE_2' }
  | { type: 'BUY_PRESTIGE_BUFF'; payload: { buffId: string } }
  | { type: 'BUY_META_UPGRADE'; payload: { upgradeId: string } }
  | { type: 'CLAIM_OFFLINE_BONUS'; payload: { watchedAd: boolean } }
  | { type: 'CLAIM_GOLDEN_EVENT'; payload: { watchedAd: boolean } }
  | { type: 'CLAIM_DAILY_BONUS' }
  | { type: 'ACTIVATE_CLICK_BOOSTER' }
  | { type: 'MARKET_MANIPULATE'; payload: { manipulationId: string } }
  | { type: 'DISMISS_NEWS' }
  | { type: 'DARKPOOL_BET'; payload: { coinId: string; amount: number } }
  | { type: 'DARKPOOL_CLAIM' }
  | { type: 'DARKPOOL_INSIDER'; payload: { watchedAd: boolean } }
  | { type: 'DARKPOOL_TUTORIAL_SEEN' }
  | { type: 'DARKPOOL_SKIP_COOLDOWN' }
  | { type: 'AD_BOOST_X5' }
  | { type: 'TIMESKIP'; payload: { seconds: number } }
  | { type: 'TAP_BOOST_X10' }
  | { type: 'PRESTIGE_AD_BONUS'; payload: { bonusBlockchain: number } }
  | { type: 'TICK'; payload: { deltaTime: number } }
  | { type: 'SET_RUNNING'; payload: { running: boolean } }

/**
 * 클릭 액션 (콤보 포함)
 */
export const handleClick: ActionHandler<GameState, Extract<GameAction, { type: 'CLICK' }>> = (
  state
) => {
  if (!state.isRunning) return null;

  const now = Date.now();
  const power = getCurrentClickPower(state);

  // 콤보 업데이트
  const timeSinceLastClick = now - state.combo.lastClickTime;
  let newComboCount = state.combo.count;

  if (timeSinceLastClick > state.combo.decayTime) {
    newComboCount = 0;
  }
  newComboCount++;

  return {
    ...state,
    coins: state.coins + power,
    totalEarnedThisRun: state.totalEarnedThisRun + power,
    combo: {
      ...state.combo,
      count: newComboCount,
      lastClickTime: now,
      maxCombo: Math.max(state.combo.maxCombo, newComboCount)
    },
    statistics: {
      ...state.statistics,
      totalClicks: state.statistics.totalClicks + 1,
      totalEarned: state.statistics.totalEarned + power
    }
  };
};

/**
 * 코인 클릭 (통계용)
 */
export const handleDogeClick: ActionHandler<GameState, Extract<GameAction, { type: 'DOGE_CLICK' }>> = (
  state
) => {
  return {
    ...state,
    statistics: {
      ...state.statistics,
      dogeClicks: state.statistics.dogeClicks + 1
    }
  };
};

/**
 * 생산기 구매
 */
export const handleBuyGenerator: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'BUY_GENERATOR' }>
> = (state, action) => {
  const { generatorId, count = 1 } = action.payload;

  const genConfig = GENERATORS.find(g => g.id === generatorId);
  if (!genConfig) return null;

  const gen = state.generators[generatorId];
  if (!gen) return null;

  const cost = getGeneratorCost(genConfig.baseCost, gen.count, genConfig.costMultiplier);
  if (state.coins < cost) return null;

  return {
    ...state,
    coins: state.coins - cost,
    generators: {
      ...state.generators,
      [generatorId]: {
        ...gen,
        count: gen.count + count
      }
    }
  };
};

/**
 * 업그레이드 구매
 */
export const handleBuyUpgrade: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'BUY_UPGRADE' }>
> = (state, action) => {
  const { upgradeId } = action.payload;

  if (state.purchasedUpgrades.includes(upgradeId)) return null;

  const upConfig = UPGRADES.find(u => u.id === upgradeId);
  if (!upConfig) return null;

  if (state.coins < upConfig.cost) return null;
  if (!isUpgradeUnlocked(upConfig, state)) return null;

  const newState = {
    ...state,
    coins: state.coins - upConfig.cost,
    purchasedUpgrades: [...state.purchasedUpgrades, upgradeId],
  };

  // 콤보 유지시간 업그레이드 즉시 적용
  if (upConfig.effect.type === 'comboDecay') {
    newState.combo = { ...newState.combo, decayTime: upConfig.effect.value };
  }

  return newState;
};

/**
 * 1차 환생 (블록체인 획득)
 */
export const handlePrestige: ActionHandler<GameState, Extract<GameAction, { type: 'PRESTIGE' }>> = (
  state
) => {
  let reward = calculateBlockchainReward(state.statistics.totalEarned);
  if (reward === 0) return null;

  // up_res_prestige_boost: 환생 블록체인 +50%
  if (state.purchasedUpgrades.includes('up_res_prestige_boost')) {
    reward = Math.floor(reward * 1.5);
  }

  // 초기 상태로 리셋 (환생 데이터만 유지)
  const generators: Record<string, any> = {};
  for (const gen of GENERATORS) {
    generators[gen.id] = { id: gen.id, count: 0 };
  }

  return {
    ...state,
    coins: 0,
    totalEarnedThisRun: 0,
    generators,
    purchasedUpgrades: [], // 업그레이드 리셋
    clickPower: 1,
    combo: {
      count: 0,
      lastClickTime: 0,
      maxCombo: state.combo.maxCombo, // 최대 콤보는 유지
      decayTime: 3000
    },
    activeGoldenEffects: [],
    clickBooster: {
      active: false,
      expiresAt: 0,
      cooldownUntil: state.clickBooster.cooldownUntil // 쿨다운은 유지
    },
    prestige: {
      ...state.prestige,
      count: state.prestige.count + 1,
      blockchain: state.prestige.blockchain + reward,
      totalBlockchainEarned: state.prestige.totalBlockchainEarned + reward
    },
    market: {
      ...state.market,
      activeManipulation: null, // 조작만 리셋
    }
  };
};

/**
 * 2차 환생 (메타버스 코인 획득)
 */
export const handlePrestige2: ActionHandler<GameState, Extract<GameAction, { type: 'PRESTIGE_2' }>> = (
  state
) => {
  if (!state.prestige2.unlocked) return null;
  if (!canPrestige2(state.prestige.blockchain)) return null;

  const reward = calculateMetaCoinReward(state.prestige.blockchain);
  if (reward === 0) return null;

  // 초기 상태로 리셋 (2차 환생 데이터만 유지)
  const generators: Record<string, any> = {};
  for (const gen of GENERATORS) {
    generators[gen.id] = { id: gen.id, count: 0 };
  }

  return {
    ...state,
    coins: 0,
    totalEarnedThisRun: 0,
    generators,
    purchasedUpgrades: [], // 업그레이드 리셋
    clickPower: 1,
    combo: {
      count: 0,
      lastClickTime: 0,
      maxCombo: state.combo.maxCombo,
      decayTime: 3000
    },
    activeGoldenEffects: [],
    clickBooster: {
      active: false,
      expiresAt: 0,
      cooldownUntil: 0
    },
    prestige: {
      count: 0,
      blockchain: 0,
      totalBlockchainEarned: 0,
      buffs: {} // 블록체인 버프도 리셋
    },
    prestige2: {
      ...state.prestige2,
      count: state.prestige2.count + 1,
      metaCoins: state.prestige2.metaCoins + reward,
      totalMetaCoinsEarned: state.prestige2.totalMetaCoinsEarned + reward
    },
    statistics: {
      ...state.statistics,
      totalEarned: 0 // 총 생산량 리셋 (2차 환생이므로)
    }
  };
};

/**
 * 블록체인 버프 구매
 */
export const handleBuyPrestigeBuff: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'BUY_PRESTIGE_BUFF' }>
> = (state, action) => {
  const { buffId } = action.payload;

  const buffConfig = PRESTIGE_BUFFS.find(b => b.id === buffId);
  if (!buffConfig) return null;

  const currentLevel = state.prestige.buffs[buffId] || 0;
  if (currentLevel >= buffConfig.maxLevel) return null;

  const cost = Math.floor(buffConfig.baseCost * Math.pow(buffConfig.costMultiplier, currentLevel));
  if (state.prestige.blockchain < cost) return null;

  return {
    ...state,
    prestige: {
      ...state.prestige,
      blockchain: state.prestige.blockchain - cost,
      buffs: {
        ...state.prestige.buffs,
        [buffId]: currentLevel + 1
      }
    }
  };
};

/**
 * 메타버스 업그레이드 구매
 */
export const handleBuyMetaUpgrade: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'BUY_META_UPGRADE' }>
> = (state, action) => {
  const { upgradeId } = action.payload;

  const upgradeConfig = META_UPGRADES.find(u => u.id === upgradeId);
  if (!upgradeConfig) return null;

  if (state.prestige2.purchasedUpgrades.includes(upgradeId)) return null; // 이미 구매
  if (state.prestige2.metaCoins < upgradeConfig.cost) return null;

  return {
    ...state,
    prestige2: {
      ...state.prestige2,
      metaCoins: state.prestige2.metaCoins - upgradeConfig.cost,
      purchasedUpgrades: [...state.prestige2.purchasedUpgrades, upgradeId]
    }
  };
};

/**
 * 오프라인 보너스 수령
 */
export const handleClaimOfflineBonus: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'CLAIM_OFFLINE_BONUS' }>
> = (state, action) => {
  if (!state.offlineBonus.pending) return null;

  const { watchedAd } = action.payload;
  let earnings = state.offlineBonus.earnings;

  if (watchedAd) {
    earnings *= 2;
  }

  return {
    ...state,
    coins: state.coins + earnings,
    totalEarnedThisRun: state.totalEarnedThisRun + earnings,
    offlineBonus: {
      pending: false,
      earnings: 0,
      duration: 0,
      capped: false
    },
    statistics: {
      ...state.statistics,
      totalEarned: state.statistics.totalEarned + earnings,
      offlineClaims: state.statistics.offlineClaims + 1,
      adsWatched: watchedAd ? state.statistics.adsWatched + 1 : state.statistics.adsWatched
    }
  };
};

/**
 * 골든 이벤트 클릭
 */
export const handleClaimGoldenEvent: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'CLAIM_GOLDEN_EVENT' }>
> = (state, action) => {
  if (!state.goldenEvent.active) return null;

  const { watchedAd } = action.payload;
  const eventType = state.goldenEvent.type;
  const eventConfig = GOLDEN_EVENTS.find(e => e.id === eventType);
  if (!eventConfig) return null;

  let adMultiplier = watchedAd ? GOLDEN_CONFIG.adMultiplier : 1;
  let adDurationMultiplier = watchedAd ? GOLDEN_CONFIG.adDurationMultiplier : 1;

  // up_res_golden_master: 골든 효과 2배 + 지속 2배
  if (state.purchasedUpgrades.includes('up_res_golden_master')) {
    adMultiplier *= 2;
    adDurationMultiplier *= 2;
  }
  // up_global_golden1: 골든 지속시간 +50%
  if (state.purchasedUpgrades.includes('up_global_golden1')) {
    adDurationMultiplier *= 1.5;
  }

  let newState = { ...state };

  // 즉시 효과
  if (eventConfig.effect.type === 'instant') {
    if (eventConfig.id === 'golden_coin') {
      const cps = getCurrentCPS(state);
      const earnings = cps * eventConfig.effect.value * adMultiplier;
      newState.coins += earnings;
      newState.totalEarnedThisRun += earnings;
      newState.statistics.totalEarned += earnings;
    } else if (eventConfig.id === 'mega_jackpot') {
      const bonus = state.coins * (eventConfig.effect.value - 1) * adMultiplier;
      newState.coins += bonus;
      newState.totalEarnedThisRun += bonus;
      newState.statistics.totalEarned += bonus;
    }
  }
  // 지속 효과
  else if (eventConfig.effect.type === 'duration') {
    const duration = (eventConfig.effect.duration || 30) * 1000 * adDurationMultiplier;
    const multiplier = eventConfig.effect.value * adMultiplier;

    newState.activeGoldenEffects = [
      ...state.activeGoldenEffects,
      {
        type: eventConfig.id,
        multiplier,
        expiresAt: Date.now() + duration
      }
    ];
  }

  // 골든 이벤트 비활성화 (출현 주기에 업그레이드 반영)
  const intervalMult = getGoldenIntervalMultiplier(state);
  const min = GOLDEN_CONFIG.minInterval * intervalMult;
  const max = GOLDEN_CONFIG.maxInterval * intervalMult;
  newState.goldenEvent = {
    active: false,
    type: null,
    spawnedAt: 0,
    expiresAt: 0,
    nextSpawnTime: Date.now() + Math.random() * (max - min) + min,
  };

  // 통계 업데이트
  newState.statistics = {
    ...newState.statistics,
    totalGoldenClicks: newState.statistics.totalGoldenClicks + 1,
    luckyGoldenCount: eventConfig.id === 'lucky_golden' ? newState.statistics.luckyGoldenCount + 1 : newState.statistics.luckyGoldenCount,
    adsWatched: watchedAd ? newState.statistics.adsWatched + 1 : newState.statistics.adsWatched
  };

  return newState;
};

/**
 * 일일 보너스 수령
 */
export const handleClaimDailyBonus: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'CLAIM_DAILY_BONUS' }>
> = (state) => {
  if (!state.dailyBonus.canClaim) return null;

  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  let newStreak = 1;
  if (state.dailyBonus.lastClaimDate === yesterday) {
    newStreak = state.dailyBonus.streak + 1;
  }

  // 보상: CPS x 3600 (1시간치)
  const cps = getCurrentCPS(state);
  const reward = cps * 3600;

  return {
    ...state,
    coins: state.coins + reward,
    totalEarnedThisRun: state.totalEarnedThisRun + reward,
    dailyBonus: {
      lastClaimDate: today,
      streak: newStreak,
      canClaim: false
    },
    statistics: {
      ...state.statistics,
      totalEarned: state.statistics.totalEarned + reward
    }
  };
};

/**
 * 클릭 부스터 활성화 (광고)
 */
export const handleActivateClickBooster: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'ACTIVATE_CLICK_BOOSTER' }>
> = (state) => {
  const now = Date.now();

  if (state.clickBooster.active) return null; // 이미 활성화
  if (now < state.clickBooster.cooldownUntil) return null; // 쿨다운 중

  return {
    ...state,
    clickBooster: {
      active: true,
      expiresAt: now + 15_000, // 15초
      cooldownUntil: now + 15_000 // 15초 쿨다운
    },
    statistics: {
      ...state.statistics,
      adsWatched: state.statistics.adsWatched + 1
    }
  };
};

/**
 * 마켓 조작 액션
 */
export const handleMarketManipulate: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'MARKET_MANIPULATE' }>
> = (state, action) => {
  if (!state.market) return null;
  const { manipulationId } = action.payload;
  const now = Date.now();

  const config = MANIPULATIONS.find(m => m.id === manipulationId);
  if (!config) return null;

  // Check cooldown
  const cooldownUntil = state.market.manipulationCooldowns[manipulationId] || 0;
  if (now < cooldownUntil) return null;

  // Check if another manipulation is active
  if (state.market.activeManipulation && now < state.market.activeManipulation.expiresAt) return null;

  // Calculate cost
  const cps = getCurrentCPS(state);
  const cost = Math.max(config.minCost, cps * config.costMultiplier);
  if (state.coins < cost) return null;

  // Determine impact (backfire check)
  let impact = config.impact;
  if (config.backfireChance > 0 && Math.random() < config.backfireChance) {
    impact = -impact; // Backfire!
  }

  return {
    ...state,
    coins: state.coins - cost,
    market: {
      ...state.market,
      activeManipulation: {
        id: config.id,
        expiresAt: now + config.duration,
        impact,
      },
      manipulationCooldowns: {
        ...state.market.manipulationCooldowns,
        [manipulationId]: now + config.cooldown,
      },
    },
  };
};

/**
 * 뉴스 닫기
 */
export const handleDismissNews: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'DISMISS_NEWS' }>
> = (state) => {
  if (!state.market?.activeNews) return null;
  return {
    ...state,
    market: {
      ...state.market,
      activeNews: null,
    },
  };
};

/**
 * 다크풀 베팅 (실제 코인으로 베팅)
 */
export const handleDarkPoolBet: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'DARKPOOL_BET' }>
> = (state, action) => {
  const dp = state.darkPool;
  if (!dp || dp.phase !== 'betting') return null;
  if (dp.playerBet) return null; // already bet

  const { coinId, amount } = action.payload;

  // Validate coin exists in this round
  if (!dp.coins.find(c => c.id === coinId)) return null;

  // Validate amount (real coins)
  const maxBet = Math.floor(state.coins * getMaxBetRatio(state.purchasedUpgrades));
  const clampedAmount = Math.min(amount, maxBet, state.coins);
  if (clampedAmount <= 0) return null;

  return {
    ...state,
    coins: state.coins - clampedAmount,
    darkPool: {
      ...dp,
      playerBet: { coinId, amount: clampedAmount },
      totalBets: dp.totalBets + 1,
    },
  };
};

/**
 * 다크풀 결과 수령 (실제 코인 보상)
 */
export const handleDarkPoolClaim: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'DARKPOOL_CLAIM' }>
> = (state) => {
  const dp = state.darkPool;
  if (!dp || dp.phase !== 'result') return null;
  if (!dp.playerBet || !dp.result) return null;

  const won = dp.playerBet.coinId === dp.result.winnerId;
  const reward = won
    ? dp.playerBet.amount * dp.result.multiplier * getRewardMultiplier(state.purchasedUpgrades)
    : 0;
  const profit = won ? reward - dp.playerBet.amount : -dp.playerBet.amount;

  return {
    ...state,
    coins: state.coins + reward,
    statistics: {
      ...state.statistics,
      totalEarned: state.statistics.totalEarned + reward,
    },
    darkPool: {
      ...dp,
      phase: 'idle',
      coins: [],
      playerBet: null,
      insiderInfo: null,
      result: null,
      _outcome: null,
      phaseEndTime: 0,
      nextRoundTime: Date.now() + DARKPOOL_CONFIG.roundCooldown,
      totalProfit: dp.totalProfit + profit,
      totalWins: won ? dp.totalWins + 1 : dp.totalWins,
    },
  };
};

/**
 * 다크풀 인사이더 정보 (광고 시청)
 */
export const handleDarkPoolInsider: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'DARKPOOL_INSIDER' }>
> = (state, action) => {
  const dp = state.darkPool;
  if (!dp || dp.phase !== 'betting') return null;
  if (dp.insiderInfo) return null; // already have info
  if (!dp._outcome) return null;
  if (!action.payload.watchedAd) return null;

  const coinIds = dp.coins.map(c => c.id);
  const info = generateInsiderInfo(coinIds, dp._outcome.winnerId, dp._outcome.multiplier, state.purchasedUpgrades);

  return {
    ...state,
    darkPool: {
      ...dp,
      insiderInfo: {
        coinId: info.coinId,
        hintMultiplier: info.hintMultiplier,
      },
    },
    statistics: {
      ...state.statistics,
      adsWatched: state.statistics.adsWatched + 1,
    },
  };
};

/**
 * 광고 부스트 x10 (30초간 CPS+클릭 x10, 쿨다운 30초)
 */
export const handleAdBoostX5: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'AD_BOOST_X5' }>
> = (state) => {
  const now = Date.now();
  if (now < state.adBoostCooldownUntil) return null;

  return {
    ...state,
    activeGoldenEffects: [
      ...state.activeGoldenEffects,
      {
        type: 'ad_income_x5',
        multiplier: 10,
        expiresAt: now + 15_000, // 15초
      },
    ],
    adBoostCooldownUntil: now + 15_000, // 15초 쿨다운
    statistics: {
      ...state.statistics,
      adsWatched: state.statistics.adsWatched + 1,
    },
  };
};

/**
 * Timeskip — add seconds worth of CPS to coins
 */
export const handleTimeskip: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'TIMESKIP' }>
> = (state, action) => {
  const cps = getCurrentCPS(state);
  const earned = Math.floor(cps * action.payload.seconds);
  if (earned <= 0) return null;

  return {
    ...state,
    coins: state.coins + earned,
    statistics: {
      ...state.statistics,
      totalEarned: state.statistics.totalEarned + earned,
      adsWatched: state.statistics.adsWatched + 1,
    },
  };
};

/**
 * Tap Boost x10 — 15s tap power x10
 */
export const handleTapBoostX10: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'TAP_BOOST_X10' }>
> = (state) => {
  const now = Date.now();
  return {
    ...state,
    activeGoldenEffects: [
      ...state.activeGoldenEffects,
      {
        type: 'ad_tap_x10',
        multiplier: 10,
        expiresAt: now + 15_000,
      },
    ],
    statistics: {
      ...state.statistics,
      adsWatched: state.statistics.adsWatched + 1,
    },
  };
};

/**
 * 환생 광고 보너스 (블록체인 추가 지급)
 */
export const handlePrestigeAdBonus: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'PRESTIGE_AD_BONUS' }>
> = (state, action) => {
  const { bonusBlockchain } = action.payload;
  if (bonusBlockchain <= 0) return null;

  return {
    ...state,
    prestige: {
      ...state.prestige,
      blockchain: state.prestige.blockchain + bonusBlockchain,
      totalBlockchainEarned: state.prestige.totalBlockchainEarned + bonusBlockchain,
    },
    statistics: {
      ...state.statistics,
      adsWatched: state.statistics.adsWatched + 1,
    },
  };
};

/**
 * 다크풀 튜토리얼 확인
 */
export const handleDarkPoolTutorialSeen: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'DARKPOOL_TUTORIAL_SEEN' }>
> = (state) => {
  if (state.darkPool?.tutorialSeen) return null;
  return {
    ...state,
    darkPool: { ...state.darkPool, tutorialSeen: true },
  };
};

/**
 * Dark Pool — Skip cooldown (ad reward)
 */
export const handleDarkPoolSkipCooldown: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'DARKPOOL_SKIP_COOLDOWN' }>
> = (state) => {
  const dp = state.darkPool;
  if (!dp || dp.phase !== 'idle') return null;
  return {
    ...state,
    darkPool: { ...dp, nextRoundTime: Date.now() },
  };
};

/**
 * 틱 액션 (자동 생산, 골든 출현 등은 시스템에서 처리)
 */
export const handleTick: ActionHandler<GameState, Extract<GameAction, { type: 'TICK' }>> = () => {
  return null; // 시스템에서 처리하므로 null 반환
};

/**
 * 실행 상태 변경
 */
export const handleSetRunning: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'SET_RUNNING' }>
> = (state, action) => {
  if (state.isRunning === action.payload.running) return null;

  return {
    ...state,
    isRunning: action.payload.running
  };
};


/**
 * 모든 액션 핸들러 맵
 */
export const actionHandlers = {
  CLICK: handleClick,
  DOGE_CLICK: handleDogeClick,
  BUY_GENERATOR: handleBuyGenerator,
  BUY_UPGRADE: handleBuyUpgrade,
  PRESTIGE: handlePrestige,
  PRESTIGE_2: handlePrestige2,
  BUY_PRESTIGE_BUFF: handleBuyPrestigeBuff,
  BUY_META_UPGRADE: handleBuyMetaUpgrade,
  CLAIM_OFFLINE_BONUS: handleClaimOfflineBonus,
  CLAIM_GOLDEN_EVENT: handleClaimGoldenEvent,
  CLAIM_DAILY_BONUS: handleClaimDailyBonus,
  ACTIVATE_CLICK_BOOSTER: handleActivateClickBooster,
  MARKET_MANIPULATE: handleMarketManipulate,
  DISMISS_NEWS: handleDismissNews,
  DARKPOOL_BET: handleDarkPoolBet,
  DARKPOOL_CLAIM: handleDarkPoolClaim,
  DARKPOOL_INSIDER: handleDarkPoolInsider,
  DARKPOOL_TUTORIAL_SEEN: handleDarkPoolTutorialSeen,
  DARKPOOL_SKIP_COOLDOWN: handleDarkPoolSkipCooldown,
  AD_BOOST_X5: handleAdBoostX5,
  TIMESKIP: handleTimeskip,
  TAP_BOOST_X10: handleTapBoostX10,
  PRESTIGE_AD_BONUS: handlePrestigeAdBonus,
  TICK: handleTick,
  SET_RUNNING: handleSetRunning,
} as const;
